import {
  genericGetSingle,
  genericCreateSingle,
  genericGetAll,
  genericDeleteSingle,
  makeAPIRequest,
} from "../helpers/apiCall";
import { Order } from "../models";
import { Error } from "../types";

/**
 * Get all orders
 * @param params URLSearchParams
 * @returns Promise<{ orders: Order[] }>
 */
function getAll(params: URLSearchParams = null): Promise<{ orders: Order[] }> {
  return genericGetAll("orders", params);
}

/**
 * Get a single order
 * @param id string
 * @returns Promise<{ order: Order }>
 */
function getSingle(id: string): Promise<{ order: Order }> {
  return genericGetSingle("orders", id);
}

/**
 * Creates a single order.
 *
 * @param order An object containing order details.
 * @returns A Promise that resolves to an object with the order or an Error.
 */
function createSingle(order: {
  [key: string]: string | number | boolean | Date | undefined;
}): Promise<{ order: Order } | Error> {
  return genericCreateSingle("orders", order);
}

/**
 * Gets the count of orders
 * @param params URLSearchParams
 * @returns Promise<{ count: number }>
 */
function getCount(params: URLSearchParams = null): Promise<{ count: number }> {
  if (!params) params = new URLSearchParams();
  params.append("countOnly", "true");
  return genericGetAll("orders", params);
}

/**
 * Allocates a single order in PowerPick via the API.
 * @param id string
 * @returns Promise with allocation details
 */
function allocate(id: string): Promise<{
  fullyAllocated: boolean;
  processable: boolean;
  status?: string;
  message?: string;
  allocatedLines: number;
}> {
  return makeAPIRequest({ url: `/orders/${id}/allocate`, method: "get" })
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
}

/**
 * Deallocates a single order in PowerPick via the API.
 * @param id string
 * @returns Promise with deallocation result
 */
function deallocate(id: string): Promise<{
  deallocated: boolean;
  message: string;
}> {
  return makeAPIRequest({ url: `/orders/${id}/deallocate`, method: "get" })
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
}

/**
 * Deletes a single order by ID.
 * @param id number | string
 * @returns Promise resolving when deletion completes
 */
function deleteSingle(id: number | string) {
  return genericDeleteSingle("orders", id);
}

export const orderService = {
  getAll,
  getSingle,
  createSingle,
  getCount,
  allocate,
  deallocate,
  deleteSingle,
};
